import React, { useEffect, useState, useMemo, useCallback, memo } from 'react';
import '../styles/Contact.css';
import { Container, Row, Col } from 'react-bootstrap';
import { FiPhone, FiMail, FiMapPin } from "react-icons/fi";
import { AiOutlineCloseCircle } from "react-icons/ai";
import emailjs from '@emailjs/browser';
import useScrollAnimation from '../hooks/useScrollAnimation';

const INPUTS_CONFIG = [
  { label: 'Name', type: 'text', placeholder: 'Your Name' },
  { label: 'Email', type: 'email', placeholder: 'Your Email' },
  { label: 'Message', type: 'textarea', placeholder: 'Your Message' },
];

const CONTACT_DETAILS = [
  { 
    label: 'Phone', 
    Icon: FiPhone,
    text: '+972 593230201',
    link: 'https://wa.me/972593230201'  
  },
  { 
    label: 'Email', 
    Icon: FiMail,
    text: 'rehammagharee003@gmail.com',
    link: 'mailto:rehammagharee003@gmail.com'  
  },
  { 
    label: 'Location', 
    Icon: FiMapPin,
    text: 'Gaza, Palestine',
  },
];

const ContactDetail = memo(function ContactDetail({ label, Icon, text, link }) {
  return (
    <div className="detail-item">
      <div className='icon-container'>
        <span className="detail-icon">
          <Icon size={25} color='#b88bfa' />
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", textAlign: "start" }}>
        <span className="detail-label">{label}</span>
        {link ? (
          <a href={link} target="_blank" rel="noopener noreferrer" className="detail-link">
            {text}
          </a>
        ) : (
          <span>{text}</span>
        )}
      </div>
    </div>
  );
});

const Contact = () => {
  const [titleRef, animate] = useScrollAnimation();
  
  const [visibleCount, setVisibleCount] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState({});

  const title = "Contact Me";

  useEffect(() => {
    if (!animate) return;
    let current = 0;
    const interval = setInterval(() => {
      current++;
      setVisibleCount(current);
      if (current === INPUTS_CONFIG.length + 1) {
        clearInterval(interval);
      }
    }, 250);

    return () => clearInterval(interval);
  }, [animate]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setIsSubmitted(prev => {
      if (prev) {
        setErrors(prevErrors => ({ ...prevErrors, [name]: !value }));
      }
      return prev;
    });
  }, []);

  const sendEmail = useCallback((e) => {
    e.preventDefault();
    setIsSubmitted(true);

    const newErrors = {};
    if (!formData.name) newErrors.name = true;
    if (!formData.email) newErrors.email = true;
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = true;
    if (!formData.message) newErrors.message = true;
    
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    emailjs.send(
      'service_2e0mkon',
      'template_fafqi78',
      {
        name: formData.name,
        email: formData.email,
        message: formData.message
      },
      'ZY8Fgh6unaD0nAiH2'
    )
    .then(() => {
      setStatusMessage('success');
      setAlertVisible(true);
      setFormData({ name: '', email: '', message: '' });
      setErrors({});
      setIsSubmitted(false);
    })
    .catch(() => {
      setStatusMessage('error');
      setAlertVisible(true);
    });
  }, [formData]);

  const closeAlert = useCallback(() => {
    setAlertVisible(false);
    setTimeout(() => setStatusMessage(''), 500);
  }, []);

  const animatedTitle = useMemo(
    () =>
      title.split("").map((char, i) => (
        <span key={i} style={{ animationDelay: `${i * 0.08}s` }} className="animated-char">
          {char === " " ? "\u00A0" : char}
        </span>
      )),
    [title]
  );

  return (
    <section id="contact" className="contact-section">
      <Container className="contact-container">
        {statusMessage && (
          <div className="alert-overlay">
            <div className={`status-alert ${statusMessage} ${alertVisible ? '' : 'hide'}`}>
              <button className="close-alert-btn" onClick={closeAlert}>×</button>
              {statusMessage === 'success' ? (
                <>
                  <p>Message sent successfully!</p>
                  <small>I will get back to you as soon as possible.</small>
                </>
              ) : (
                <p>Failed to send message. Please try again later.</p>
              )}
            </div>
          </div>
        )}

        <h2 ref={titleRef} className={`animated-title ${animate ? "show" : ""}`}>
          {animatedTitle}
        </h2>

        <Row>
          <Col md={6} className='f-col'>
            <p className="contact-para">Feel free to reach out to me anytime!</p>
            <div className="details-box">
              {CONTACT_DETAILS.map((detail, index) => (
                <ContactDetail key={index} {...detail} />
              ))}
            </div>
          </Col>

          <Col md={6} className='s-col'>
            <form onSubmit={sendEmail} className="inputs-box" noValidate>
              {INPUTS_CONFIG.map((input, index) => {
                const fieldName = input.label.toLowerCase();
                const hasError = errors[fieldName];

                return (
                  <div
                    key={index}
                    className={`input-wrapper ${visibleCount > index ? "visible" : "hidden"}`}
                    style={{ transitionDelay: `${index * 250}ms`, marginBottom: '10px' }}
                  >
                    {input.type === 'textarea' ? (
                      <textarea
                        placeholder={input.placeholder}
                        rows="3"
                        className="input-field"
                        name={fieldName}
                        value={formData[fieldName]}
                        onChange={handleChange}
                        style={{height:"150px"}}
                      />
                    ) : (
                      <input
                        type={input.type}
                        placeholder={input.placeholder}
                        className="input-field"
                        name={fieldName}
                        value={formData[fieldName]}
                        onChange={handleChange}
                      />
                    )}

                    {isSubmitted && hasError && (
                      <AiOutlineCloseCircle className="input-icon invalid-icon" />
                    )}
                  </div>
                );
              })}
              <div
                className={`btn-container ${visibleCount > INPUTS_CONFIG.length ? "visible" : "hidden"}`}
                style={{ transitionDelay: `${INPUTS_CONFIG.length * 250}ms` }}
              >
                <button type="submit" className="send-button">Send Message</button>
              </div>
            </form>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Contact;
